import { useEffect, useMemo, useRef, useState } from 'react'

import { decodeLogBacklog, decodeLogFrame, logKey, parseJson } from './parse.ts'
import { useSseConnection } from './useSseConnection.ts'
import type { ConnectionState, LogFilter, LogFrame, LogRow } from './types.ts'

export const LOG_STREAM_URL = '/api/admin/stream/logs'

/**
 * Silêncio tolerado no stream de logs.
 *
 * TRÊS vezes a cadência do keepalive (15 s), e não os 10 s do stream de métricas:
 * aquele emite a cada 2 s incondicionalmente, então 10 s de silêncio é falha.
 * Aqui um filtro apontado para um agente quieto legitimamente não tem nada a
 * dizer por minutos, e quem prova vivacidade é o keepalive (§4.4).
 */
export const LOG_STALE_AFTER_MS = 45_000

/**
 * Linhas retidas na tela.
 *
 * 2.000 é ~25 s de firehose sem filtro e horas de um agente só. Acima disso o
 * navegador começa a engasgar no scroll, e o que passou já está no histórico —
 * alcançável por GET /api/admin/logs, que é completo em qualquer topologia (§4.8).
 */
export const LOG_BUFFER = 2_000

export interface LogStreamState {
  connection: ConnectionState
  rows: readonly LogRow[]
  /** O último quadro, para a barra de vazão e os avisos de cobertura. */
  frame: LogFrame | null
  /** True quando o backfill falhou. Ver §4.7. */
  backlogDegraded: boolean
  /** Payloads recusados pelos decodificadores, para o rodapé. */
  rejected: number
  /** Relógio do navegador da última vez que o stream deu sinal de vida. */
  lastEventAt: number | null
}

/**
 * Monta a URL do tail. `withTenant: false` é a rota de cliente
 * (`/api/tenant/stream/logs`), que nunca recebe empresa (§5.11): o `tenantId` do
 * filtro é descartado ali, não importa de onde tenha vindo.
 */
export function logStreamUrl(base: string, filter: LogFilter, withTenant: boolean): string {
  // ⚠️ Chaves camelCase mesmo com corpos snake_case (§0.1).
  const params = new URLSearchParams()
  if (withTenant && filter.tenantId) params.set('tenantId', filter.tenantId)
  if (filter.endpointId) params.set('endpointId', filter.endpointId)
  if (filter.eventType) params.set('eventType', filter.eventType)
  if (filter.source) params.set('source', filter.source)
  const qs = params.toString()
  return qs === '' ? base : `${base}?${qs}`
}

const adminStreamUrl = (filter: LogFilter): string => logStreamUrl(LOG_STREAM_URL, filter, true)

/** `toUrl` vem da fonte de telemetria (console × cliente); precisa ser estável. */
export function useLogStream(
  filter: LogFilter,
  toUrl: (filter: LogFilter) => string = adminStreamUrl,
): LogStreamState {
  // A URL é a IDENTIDADE do stream: mudar de filtro fecha e reabre. Memoizar é o
  // que impede um render de reabrir a conexão — e cada reabertura é uma linha no
  // log de auditoria do servidor (§0.4).
  const url = useMemo(
    () => toUrl(filter),
    [toUrl, filter.tenantId, filter.endpointId, filter.eventType, filter.source],
  )

  const [rows, setRows] = useState<readonly LogRow[]>([])
  const [frame, setFrame] = useState<LogFrame | null>(null)
  const [backlogDegraded, setBacklogDegraded] = useState(false)
  const [rejected, setRejected] = useState(0)

  // Dedupe entre backlog e tail (§4.6). Ref, não state: muda por quadro e não
  // deve provocar render por si.
  const seen = useRef<Set<string>>(new Set())

  useEffect(() => {
    setRows([])
    setFrame(null)
    setBacklogDegraded(false)
    seen.current = new Set()
  }, [url])

  const append = (incoming: readonly LogRow[]): void => {
    if (incoming.length === 0) return

    const fresh: LogRow[] = []
    for (const row of incoming) {
      const key = logKey(row)
      if (seen.current.has(key)) continue
      seen.current.add(key)
      fresh.push(row)
    }
    if (fresh.length === 0) return

    setRows((prev) => {
      // Mais novas primeiro, e o excesso sai pela cauda.
      const next = [...fresh.reverse(), ...prev]
      if (next.length <= LOG_BUFFER) return next

      const dropped = next.slice(LOG_BUFFER)
      for (const row of dropped) seen.current.delete(logKey(row))
      return next.slice(0, LOG_BUFFER)
    })
  }

  const core = useSseConnection({
    url,
    staleAfterMs: LOG_STALE_AFTER_MS,
    // Qualquer tráfego conta como sinal de vida — inclusive um evento cujo nome
    // mudou no servidor e que este cliente ainda não conhece (§4.4).
    touchOnAnyMessage: true,
    hasData: () => frame !== null,
    events: {
      backlog: (raw) => {
        const decoded = decodeLogBacklog(parseJson(raw))
        if (decoded === null) {
          setRejected((n) => n + 1)
          return true
        }
        setBacklogDegraded(decoded.degraded)
        // Chega mais recente primeiro; a lista também é mais recente primeiro.
        append([...decoded.rows].reverse())
        return true
      },
      logs: (raw) => {
        const decoded = decodeLogFrame(parseJson(raw))
        if (decoded === null) {
          setRejected((n) => n + 1)
          return true
        }
        setFrame(decoded)
        append(decoded.rows)
        return true
      },
    },
  })

  return {
    connection: core.connection,
    rows,
    frame,
    backlogDegraded,
    rejected,
    lastEventAt: core.lastEventAt,
  }
}
